/**
 * Dashboard documents of the three typologies (SPEC-0060 §4.4, rule SET-06): published rows,
 * personal copies, the user's pointer and the synthesised OFFOLIO standard. Pure helpers over the
 * rows of the user-preferences store; the document shape is the integration contract §4.
 */
import {
  DASHBOARD_TYPOLOGY,
  OFFOLIO_STANDARD_DASHBOARD_KEY,
  DASHBOARD_KEY_PATTERN,
  dashboardPointerPrefKey,
} from "../../services/user-preferences/preferenceKeys";
import { PANEL_IDS, sanitizeGeometry, sanitizeHiddenFor, sanitizeLayout } from "./analyticsLayout";
import { sanitizeChartOptions } from "./analyticsChartOptions";
import { isColorToken } from "../ChartSeriesUtils/seriesStyle";

export const OFFOLIO_STANDARD_NAME = "OFFOLIO standard";

/** The Roadmap typology (SPEC-0060 §4.4 point 1) has one chart whose objective a publisher may write: its filters and purpose are the document. */
export const ROADMAP_PANEL_IDS = ["roadmapUsages"];

/** Panels of the project's Monitoring metrics typology (SPEC-0060 §4.2): tiles, charts and EVM panels. */
export const MONITORING_PANEL_IDS = [
  "duration",
  "work",
  "laborCost",
  "cost",
  "globalCost",
  "chanceOfSuccess",
  "impactChart",
  "usageChart",
  "feverChart",
  "scenarioResourceBudget",
  "evmContent",
  "costVariance",
  "costPerformanceIndex",
  "costIndicator",
  "scheduleVariance",
  "schedulePerformanceIndex",
  "scheduleIndicator",
  "budgetAtCompletion",
  "estimateAtCompletion",
];

const PANELS_BY_TYPOLOGY = {
  [DASHBOARD_TYPOLOGY.RESOURCE_CAPACITY]: PANEL_IDS,
  [DASHBOARD_TYPOLOGY.MONITORING]: MONITORING_PANEL_IDS,
  [DASHBOARD_TYPOLOGY.ROADMAP]: ROADMAP_PANEL_IDS,
};

export const panelIdsOf = (typology) => PANELS_BY_TYPOLOGY[typology] || [];

/** One reducer slot per (scope, typology): a portfolio and a project document live side by side. */
export const docKeyOf = (scope, typology) => `${scope}/${typology}`;

const prefix = (typology) => `dashboard:${typology}:`;
const keyOfPref = (prefKey, typology) => prefKey.slice(prefix(typology).length);

const MAX_NAME = 120;
const MAX_PURPOSE = 1000;
const str = (v, max) => (typeof v === "string" ? v.slice(0, max) : null);

const sanitizeSeriesStyles = (raw) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out = {};
  Object.entries(raw).forEach(([fieldKey, list]) => {
    if (typeof fieldKey !== "string" || !fieldKey || !Array.isArray(list)) return;
    const styles = list
      .filter((s) => s && s.valueId != null && Number.isFinite(Number(s.rank)))
      .map((s) => ({
        valueId: String(s.valueId),
        rank: Number(s.rank),
        colorToken: isColorToken(s.colorToken) ? s.colorToken : undefined,
        hidden: s.hidden === true,
      }));
    out[fieldKey] = styles;
  });
  return out;
};

const sanitizeFilters = (raw) =>
  (Array.isArray(raw) ? raw : []).filter(
    (f) => f && typeof f.target === "string" && f.attributeId != null && Array.isArray(f.values)
  );

const sanitizePurpose = (raw, panelIds) => {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out = {};
  panelIds.forEach((id) => {
    const p = raw[id];
    if (!p || typeof p !== "object") return;
    const en = str(p.en, MAX_PURPOSE);
    const fr = str(p.fr, MAX_PURPOSE);
    if (en || fr) out[id] = { en: en || "", fr: fr || "" };
  });
  return out;
};

const sanitizeSource = (raw) =>
  raw && typeof raw === "object" && typeof raw.dashboardKey === "string" && Number.isInteger(raw.version)
    ? { dashboardKey: raw.dashboardKey, version: raw.version }
    : undefined;

/**
 * A dashboard document as saved: nothing outside the contract, nothing malformed. The Resource &
 * capacity layout is validated against its six panels; the project's layout is kept as geometry
 * only (its EVM panels depend on the data) and applied with limits at render time.
 */
export const sanitizeDashboardDocument = (raw, typology, { withPrevious = true } = {}) => {
  const r = raw && typeof raw === "object" ? raw : {};
  const panelIds = panelIdsOf(typology);
  const doc = {
    name: str(r.name, MAX_NAME) || "",
    builtin: r.builtin === true,
    default: Boolean(r.default),
    archived: r.archived === true,
    layout:
      r.layout == null
        ? null
        : typology === DASHBOARD_TYPOLOGY.RESOURCE_CAPACITY
          ? sanitizeLayout(r.layout)
          : sanitizeGeometry(r.layout),
    hidden: sanitizeHiddenFor(r.hidden, panelIds),
    chartOptions: sanitizeChartOptions(r.chartOptions, panelIds),
    filters: sanitizeFilters(r.filters),
    seriesStyles: sanitizeSeriesStyles(r.seriesStyles),
    purpose: sanitizePurpose(r.purpose, panelIds),
  };
  const source = sanitizeSource(r.source);
  if (source) doc.source = source;
  // `hideDescriptions` and `datePreset` written by earlier builds are dropped: the user's own, in their own families.
  if (withPrevious && r.previous && typeof r.previous === "object") {
    doc.previous = { ...sanitizeDashboardDocument(r.previous, typology, { withPrevious: false }) };
    if (typeof r.previous.takenAt === "string") doc.previous.takenAt = r.previous.takenAt;
  }
  return doc;
};

/** The OFFOLIO standard when no backend row exists: today's page, unchanged. */
export const synthesiseStandard = () => ({
  dashboardKey: OFFOLIO_STANDARD_DASHBOARD_KEY,
  published: true,
  builtin: true,
  version: 1,
  updatedAt: null,
  updatedBy: null,
  state: {
    name: OFFOLIO_STANDARD_NAME,
    builtin: true,
    default: true,
    archived: false,
    layout: null,
    hidden: [],
    chartOptions: {},
    filters: [],
    seriesStyles: {},
    purpose: {},
  },
});

const toEntry = (row, typology) => ({
  dashboardKey: keyOfPref(row.prefKey, typology),
  published: row.published === true,
  builtin: row.state?.builtin === true || keyOfPref(row.prefKey, typology) === OFFOLIO_STANDARD_DASHBOARD_KEY,
  version: Number.isInteger(row.version) ? row.version : 1,
  updatedAt: row.updatedAt || null,
  updatedBy: row.updatedBy || null,
  state: sanitizeDashboardDocument(row.state, typology),
});

const byName = (a, b) => String(a.state.name).localeCompare(String(b.state.name), undefined, { sensitivity: "base" });

/** Default first, then by name, the built-in standard last. */
export const sortDashboardRows = (rows) =>
  [...rows].sort((a, b) => {
    if (a.builtin !== b.builtin) return a.builtin ? 1 : -1;
    if (Boolean(a.state.default) !== Boolean(b.state.default)) return a.state.default ? -1 : 1;
    return byName(a, b);
  });

const usable = (entry) => entry && !entry.state.archived;

/**
 * The dashboards of one scope and typology from the store's rows, and the one the user lands on:
 * pointer → the followed published row or the user's copy → the default published row → the
 * OFFOLIO standard (a backend row when migrated, synthesised otherwise). Returns
 * `{ published, mine, pointer, current }`, `current` = `{ ...entry, follows }`.
 */
export const resolveCurrentDashboard = ({ rows, typology }) => {
  const own = (rows || []).filter((r) => r && typeof r.prefKey === "string" && r.prefKey.startsWith(prefix(typology)));
  const pointerRow = own.find((r) => !r.published && r.prefKey === dashboardPointerPrefKey(typology));
  const pointer =
    pointerRow && pointerRow.state && typeof pointerRow.state.dashboardKey === "string"
      ? { dashboardKey: pointerRow.state.dashboardKey, follows: pointerRow.state.follows !== false }
      : null;
  const publishedRows = own.filter((r) => r.published).map((r) => toEntry(r, typology));
  if (!publishedRows.some((p) => p.dashboardKey === OFFOLIO_STANDARD_DASHBOARD_KEY)) {
    publishedRows.push(synthesiseStandard());
  }
  const published = sortDashboardRows(publishedRows);
  const mine = own
    .filter((r) => !r.published && r.prefKey !== dashboardPointerPrefKey(typology))
    .map((r) => toEntry(r, typology))
    .sort(byName);

  const find = (list, key) => list.find((e) => e.dashboardKey === key);
  let current = null;
  if (pointer) {
    const target = pointer.follows ? find(published, pointer.dashboardKey) : find(mine, pointer.dashboardKey);
    if (pointer.follows ? usable(target) : target) current = { ...target, follows: pointer.follows };
  }
  if (!current) {
    const def = published.find((p) => p.state.default && usable(p) && !p.builtin);
    current = { ...(def || find(published, OFFOLIO_STANDARD_DASHBOARD_KEY)), follows: true };
  }
  return { published, mine, pointer, current };
};

/** A copy whose source has been published again since. */
export const hasUpdateAvailable = (current, published) => {
  if (!current || current.published || !current.state?.source) return false;
  const src = (published || []).find((p) => p.dashboardKey === current.state.source.dashboardKey);
  return Boolean(src) && src.version > current.state.source.version;
};

const withoutMeta = ({ previous, takenAt, ...rest }) => rest;

/** The user's copy of a published entry: its content, the user's name for it, the source version. */
export const makeCopyDocument = (publishedEntry, nameOf) => ({
  ...withoutMeta(publishedEntry.state),
  name: nameOf(publishedEntry.state.name),
  builtin: false,
  default: false,
  archived: false,
  source: { dashboardKey: publishedEntry.dashboardKey, version: publishedEntry.version },
});

/** The copy on the new published version; the copy as it was is kept one level under `previous`. */
export const takeNewVersionDocument = (copy, publishedEntry, takenAt) => ({
  ...withoutMeta(publishedEntry.state),
  name: copy.name,
  builtin: false,
  default: false,
  archived: false,
  source: { dashboardKey: publishedEntry.dashboardKey, version: publishedEntry.version },
  previous: { ...withoutMeta(copy), takenAt },
});

/** One click back: the copy as it was before "Take the new version". */
export const restorePreviousDocument = (copy) => (copy?.previous ? withoutMeta(copy.previous) : copy);

/** The copy's content replaced by the published version (Reset to the published version), name kept. */
export const resetToPublishedDocument = (copy, publishedEntry) => ({
  ...withoutMeta(publishedEntry.state),
  name: copy.name,
  builtin: false,
  default: false,
  archived: false,
  source: { dashboardKey: publishedEntry.dashboardKey, version: publishedEntry.version },
});

const MAX_KEY = 40;

/** A valid, unused dashboard key from a name; never the reserved built-in key. */
export const dashboardKeyFromName = (name, existingKeys) => {
  let base = String(name || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, MAX_KEY)
    .replace(/-+$/g, "");
  if (!base || !DASHBOARD_KEY_PATTERN.test(base) || base === OFFOLIO_STANDARD_DASHBOARD_KEY) base = base ? `${base.slice(0, MAX_KEY - 2)}-1` : "dashboard";
  const taken = new Set(existingKeys || []);
  if (!taken.has(base)) return base;
  for (let i = 2; ; i++) {
    const suffix = `-${i}`;
    const candidate = `${base.slice(0, MAX_KEY - suffix.length)}${suffix}`;
    if (!taken.has(candidate)) return candidate;
  }
};

const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** What differs between two documents (See what changed): one entry per kind, the panels touched. */
export const diffDashboards = (a, b) => {
  const out = [];
  if (!same(a.name, b.name)) out.push({ kind: "name", from: a.name, to: b.name });
  if (!same(a.layout, b.layout)) out.push({ kind: "layout" });
  if (!same(a.hidden, b.hidden)) out.push({ kind: "hidden", from: a.hidden || [], to: b.hidden || [] });
  const panels = Array.from(new Set([...Object.keys(a.chartOptions || {}), ...Object.keys(b.chartOptions || {})])).filter(
    (id) => !same(a.chartOptions?.[id], b.chartOptions?.[id])
  );
  if (panels.length) out.push({ kind: "chartOptions", panels });
  if (!same(a.filters, b.filters)) out.push({ kind: "filters" });
  if (!same(a.seriesStyles, b.seriesStyles)) out.push({ kind: "seriesStyles" });
  if (!same(a.purpose, b.purpose)) out.push({ kind: "purpose" });
  return out;
};
